// src/components/SearchResults.jsx
import React, { useState } from 'react';
import { formatDate, formatPrice, getFieldValue, isControlledSubstance, getScheduleInfo } from '../utils';
import { findIndications } from '../data/indications';

const SearchResults = ({ results, loading, error, metadata }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-700">Searching medicines...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <i className="fas fa-exclamation-circle text-red-500"></i>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!results || results.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="mb-4">
          <i className="fas fa-search text-gray-400 text-4xl"></i>
        </div>
        <p className="text-gray-700">No medicines found matching your search criteria.</p>
        <p className="text-gray-500 text-sm mt-2">Try using different keywords or adjusting your filters.</p>
      </div>
    );
  }

  // Handle expanding/collapsing a row
  const toggleRowExpansion = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  // Open the detail modal for a specific item
  const openDetailModal = (item) => {
    setDetailModal(item);
  };

  // Close the detail modal
  const closeDetailModal = () => {
    setDetailModal(null);
  };

  // Get cache information display
  const getCacheInfo = () => {
    if (!metadata) return null;
    
    if (metadata.source === 'cache') {
      const cacheDate = new Date(metadata.cache_date);
      const now = new Date();
      const hoursSinceCache = Math.round((now - cacheDate) / (1000 * 60 * 60));
      
      return (
        <div className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center">
          <i className="fas fa-database mr-1"></i>
          Cached {hoursSinceCache < 1 ? 'just now' : `${hoursSinceCache}h ago`}
        </div>
      );
    }
    
    return (
      <div className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full flex items-center">
        <i className="fas fa-sync-alt mr-1"></i>
        Fresh data
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Results count and metadata */}
      <div className="flex flex-wrap justify-between items-center gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{results.length} medicines found</span>
          {results.some(item => isControlledSubstance(item)) && (
            <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full">
              {results.filter(item => isControlledSubstance(item)).length} controlled
            </span>
          )}
          {getCacheInfo()}
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={() => window.print()}
            className="text-sm px-3 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
          >
            <i className="fas fa-print mr-1"></i> Print
          </button>
          
          <button 
            onClick={() => {
              // Create CSV data
              const headers = ['Name', 'Dosage', 'Form', 'Price', 'Manufacturer', 'Status'];
              const csvData = [
                headers.join(','),
                ...results.map(item => [
                  `"${getFieldValue(item, ['name', 'display_name', 'medicine_name', 'mpr_name', 'sahpra_product_name'], '')}"`,
                  `"${getFieldValue(item, ['dosage'], '')}"`,
                  `"${getFieldValue(item, ['dosage_form'], '')}"`,
                  `"${formatPrice(getFieldValue(item, ['sep'], ''))}"`,
                  `"${getFieldValue(item, ['manufacturer', 'applicant_name'], '')}"`,
                  `"${getFieldValue(item, ['status'], '')}"`,
                ].join(','))
              ].join('\n');
              
              // Create downloadable link
              const blob = new Blob([csvData], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.setAttribute('href', url);
              a.setAttribute('download', `medicines_${new Date().toISOString().slice(0, 10)}.csv`);
              a.click();
            }}
            className="text-sm px-3 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200"
          >
            <i className="fas fa-file-export mr-1"></i> Export CSV
          </button>
        </div>
      </div>

      {/* Results cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {results.map((item, index) => {
          // Determine if this is a controlled substance
          const isControlled = isControlledSubstance(item);
          const scheduleInfo = getScheduleInfo(item);
          
          // Get medicine name from various possible fields
          const medicineName = getFieldValue(item, ['name', 'display_name', 'medicine_name', 'mpr_name', 'sahpra_product_name']);
          
          return (
            <div 
              key={index} 
              className={`border rounded-lg overflow-hidden shadow-sm transition-all ${
                isControlled ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'
              } ${expandedRow === index ? 'shadow-md' : ''}`}
            >
              {/* Header section */}
              <div 
                className="px-4 py-3 bg-gray-50 flex justify-between items-center cursor-pointer"
                onClick={() => toggleRowExpansion(index)}
              >
                <div className="font-medium">
                  {medicineName}
                  {isControlled && scheduleInfo && (
                    <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full">
                      {scheduleInfo}
                    </span>
                  )}
                </div>
                <button className="text-gray-500 hover:text-gray-700">
                  <i className={`fas fa-chevron-${expandedRow === index ? 'up' : 'down'}`}></i>
                </button>
              </div>
              
              {/* Basic info always visible */}
              <div className="p-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  <div>
                    <div className="text-xs text-gray-500">Dosage</div>
                    <div className="text-sm">{getFieldValue(item, ['dosage'])}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Form</div>
                    <div className="text-sm">{getFieldValue(item, ['dosage_form'])}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Price</div>
                    <div className="text-sm">{formatPrice(getFieldValue(item, ['sep']))}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Manufacturer</div>
                    <div className="text-sm">{getFieldValue(item, ['manufacturer', 'applicant_name', 'applicantName'])}</div>
                  </div>
                  {(() => {
                    const uses = [...new Set(findIndications(item).map(i => i.uses))].join('; ');
                    return uses ? (
                      <div className="col-span-2">
                        <div className="text-xs text-gray-500">
                          Commonly used for
                          <span
                            className="ml-1 text-gray-400 cursor-help"
                            title="General reference only — not a complete indications list and not medical advice. Always check the package insert or ask a healthcare professional."
                          >
                            <i className="fas fa-info-circle"></i>
                          </span>
                        </div>
                        <div className="text-sm text-blue-900">{uses}</div>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
              
              {/* Expanded details */}
              {expandedRow === index && (
                <div className="p-4 bg-gray-50 border-t border-gray-200 animated fadeIn">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    <div>
                      <div className="text-xs text-gray-500">Registration No.</div>
                      <div className="text-sm">{getFieldValue(item, ['regNo', 'licence_no'])}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">NAPPI Code</div>
                      <div className="text-sm">{getFieldValue(item, ['nappi_code'])}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Status</div>
                      <div className="text-sm">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          item.status === 'Registered' || item.status === 'Available' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {getFieldValue(item, ['status'])}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Approval Date</div>
                      <div className="text-sm">{formatDate(getFieldValue(item, ['approvalDate', 'reg_date']))}</div>
                    </div>
                    {(getFieldValue(item, ['ingredient', 'active_ingredient']) !== 'N/A') && (
                      <div className="col-span-2">
                        <div className="text-xs text-gray-500">Active Ingredient</div>
                        <div className="text-sm">{getFieldValue(item, ['ingredient', 'active_ingredient'])}</div>
                      </div>
                    )}
                    {(getFieldValue(item, ['number_of_generics']) !== 'N/A') && (
                      <div>
                        <div className="text-xs text-gray-500">Generic Options</div>
                        <div className="text-sm">{getFieldValue(item, ['number_of_generics'])}</div>
                      </div>
                    )}
                    {item.cache_timestamp && (
                      <div>
                        <div className="text-xs text-gray-500">Last Updated</div>
                        <div className="text-sm">{formatDate(item.cache_timestamp)}</div>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 flex justify-end space-x-2">
                    <button 
                      onClick={() => openDetailModal(item)}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      <i className="fas fa-info-circle mr-1"></i> Full Details
                    </button>
                    <button 
                      onClick={() => {
                        // Create a formatted text export of this medicine
                        const lines = [
                          `Medicine: ${getFieldValue(item, ['name', 'display_name', 'medicine_name'])}`,
                          `Dosage: ${getFieldValue(item, ['dosage'])}`,
                          `Form: ${getFieldValue(item, ['dosage_form'])}`,
                          `Price: ${formatPrice(getFieldValue(item, ['sep']))}`,
                          `Manufacturer: ${getFieldValue(item, ['manufacturer', 'applicant_name'])}`,
                          `Registration: ${getFieldValue(item, ['regNo', 'licence_no'])}`,
                          `Status: ${getFieldValue(item, ['status'])}`,
                          `Approved: ${formatDate(getFieldValue(item, ['approvalDate', 'reg_date']))}`,
                          `Ingredients: ${getFieldValue(item, ['ingredient', 'active_ingredient'])}`,
                        ].join('\n');
                        
                        // Create a downloadable text file
                        const blob = new Blob([lines], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.setAttribute('href', url);
                        a.setAttribute('download', `${item.medicine_name || 'medicine'}_details.txt`);
                        a.click();
                      }}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    >
                      <i className="fas fa-file-export mr-1"></i> Export
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Detail modal */}
      {detailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-medium">
                {getFieldValue(detailModal, ['name', 'display_name', 'medicine_name', 'mpr_name', 'sahpra_product_name'])}
              </h3>
              <button 
                onClick={closeDetailModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Medicine Information</h4>
                  <dl className="space-y-2">
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Name</dt>
                      <dd className="text-sm text-gray-900 col-span-2">
                        {getFieldValue(detailModal, ['name', 'display_name', 'medicine_name', 'mpr_name', 'sahpra_product_name'])}
                      </dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Dosage</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{getFieldValue(detailModal, ['dosage'])}</dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Form</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{getFieldValue(detailModal, ['dosage_form'])}</dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Active Ingredient</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{getFieldValue(detailModal, ['ingredient', 'active_ingredient'])}</dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Schedule</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{getScheduleInfo(detailModal) || 'Not Scheduled'}</dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Price (SEP)</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{formatPrice(getFieldValue(detailModal, ['sep']))}</dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Generic Options</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{getFieldValue(detailModal, ['number_of_generics'])}</dd>
                    </div>
                  </dl>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Regulatory Information</h4>
                  <dl className="space-y-2">
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Registration No.</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{getFieldValue(detailModal, ['regNo', 'licence_no'])}</dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">NAPPI Code</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{getFieldValue(detailModal, ['nappi_code'])}</dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Status</dt>
                      <dd className="text-sm text-gray-900 col-span-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          detailModal.status === 'Registered' || detailModal.status === 'Available' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {getFieldValue(detailModal, ['status'])}
                        </span>
                      </dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Approval Date</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{formatDate(getFieldValue(detailModal, ['approvalDate', 'reg_date']))}</dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Manufacturer</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{getFieldValue(detailModal, ['manufacturer', 'applicant_name', 'applicantName'])}</dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Last Updated</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{formatDate(detailModal.last_updated || detailModal.cache_timestamp)}</dd>
                    </div>
                    <div className="grid grid-cols-3">
                      <dt className="text-sm text-gray-500">Data Sources</dt>
                      <dd className="text-sm text-gray-900 col-span-2">
                        <div className="flex flex-wrap gap-1">
                          {detailModal.mpr_id && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">MPR</span>
                          )}
                          {detailModal.secure_id && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">SAHPRA</span>
                          )}
                        </div>
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
              
              {/* Additional information section if available */}
              {isControlledSubstance(detailModal) && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2">Controlled Substance Notice</h4>
                  <p className="text-sm">This medicine is classified as {getScheduleInfo(detailModal)} under South African law. Special regulations apply to its prescription, dispensing, and record-keeping.</p>
                </div>
              )}
              
              {/* Actions section */}
              <div className="mt-6 flex justify-end space-x-3">
                <button 
                  onClick={closeDetailModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  Close
                </button>
                <button 
                  onClick={() => {
                    // Create a formatted text export of this medicine
                    const lines = [
                      `Medicine: ${getFieldValue(detailModal, ['name', 'display_name', 'medicine_name'])}`,
                      `Dosage: ${getFieldValue(detailModal, ['dosage'])}`,
                      `Form: ${getFieldValue(detailModal, ['dosage_form'])}`,
                      `Price: ${formatPrice(getFieldValue(detailModal, ['sep']))}`,
                      `Manufacturer: ${getFieldValue(detailModal, ['manufacturer', 'applicant_name'])}`,
                      `Registration: ${getFieldValue(detailModal, ['regNo', 'licence_no'])}`,
                      `Status: ${getFieldValue(detailModal, ['status'])}`,
                      `Approved: ${formatDate(getFieldValue(detailModal, ['approvalDate', 'reg_date']))}`,
                      `Ingredients: ${getFieldValue(detailModal, ['ingredient', 'active_ingredient'])}`,
                    ].join('\n');
                    
                    // Create a downloadable text file
                    const blob = new Blob([lines], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.setAttribute('href', url);
                    a.setAttribute('download', `${detailModal.medicine_name || 'medicine'}_details.txt`);
                    a.click();
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  <i className="fas fa-file-export mr-1"></i> Export Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResults;